import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Utensils, Dumbbell, Clock, Check, X, 
  RefreshCw, ChevronRight, Leaf, Zap, Flame, Moon, Sun,
  Info, AlertTriangle, Target, TrendingUp, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Focus mode colors and icons
const focusModeConfig = {
  stability: { bg: 'bg-[#8BB89F]/10', text: 'text-[#8BB89F]', border: 'border-[#8BB89F]/30', icon: Leaf, label: 'Stability' },
  reset: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/30', icon: RefreshCw, label: 'Reset' },
  fueling: { bg: 'bg-[#F8B64C]/10', text: 'text-[#F8B64C]', border: 'border-[#F8B64C]/30', icon: Sun, label: 'Fueling' },
  strength: { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/30', icon: Zap, label: 'Strength' },
  conditioning: { bg: 'bg-[#D54B48]/10', text: 'text-[#D54B48]', border: 'border-[#D54B48]/30', icon: Flame, label: 'Conditioning' },
};

const mealIcons = {
  breakfast: Moon,
  lunch: Sun,
  dinner: Utensils,
  snack: Utensils,
};

export default function DailyPlanPage() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState('A'); // A = Primary, B = Backup
  const [completedItems, setCompletedItems] = useState({ meals: [], workouts: [] });
  const [loggingItem, setLoggingItem] = useState(null);

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/coach360/day-plan`);
      setPlan(response.data);
    } catch (error) {
      console.error('Failed to fetch plan:', error);
      toast.error('Failed to load daily plan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlan();
  }, []);

  const handleVariantChange = async (variant) => {
    setSelectedVariant(variant);
    // Log the selection
    try {
      await axios.post(`${API}/coach360/select-plan-variant`, {
        selected_variant: variant,
        reason: variant === 'B' ? 'User selected backup plan' : 'User selected primary plan'
      });
    } catch (e) {
      console.error('Failed to log variant selection:', e);
    }
  };

  const getCurrentPlan = () => {
    if (!plan) return { meals: [], workout: null };
    return selectedVariant === 'A' ? plan.primary_plan : plan.backup_plan;
  };

  const handleLogMeal = async (meal, skipped = false) => {
    setLoggingItem(meal.title);
    try {
      await axios.post(`${API}/coach360/log-meal`, {
        meal_type: meal.meal_type,
        meal_title: meal.title,
        skipped,
        swapped: false
      });
      setCompletedItems(prev => ({
        ...prev,
        meals: [...prev.meals, meal.title]
      }));
      toast.success(skipped ? 'Meal skipped' : 'Meal logged! 🍽️');
    } catch (error) {
      toast.error('Failed to log meal');
    } finally {
      setLoggingItem(null);
    }
  };

  const handleLogWorkout = async (workout, completed = true) => {
    setLoggingItem(workout.title);
    try {
      await axios.post(`${API}/coach360/log-workout`, {
        workout_title: workout.title,
        completed,
        partial: false,
        duration_minutes: workout.duration_minutes
      });
      setCompletedItems(prev => ({
        ...prev,
        workouts: [...prev.workouts, workout.title]
      }));
      toast.success(completed ? 'Workout completed! 💪' : 'Workout skipped');
    } catch (error) {
      toast.error('Failed to log workout');
    } finally {
      setLoggingItem(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="dayplan-loading">
        <RefreshCw size={32} className="text-primary animate-spin" />
      </div>
    );
  }

  const focusMode = plan?.focus_mode || 'fueling';
  const config = focusModeConfig[focusMode] || focusModeConfig.fueling;
  const FocusIcon = config.icon;
  const currentPlan = getCurrentPlan();

  return (
    <div className="min-h-screen bg-background pb-nav" data-testid="daily-plan-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="rounded-full"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-lg">Today&apos;s Plan</h1>
              <p className="text-xs text-muted-foreground capitalize flex items-center gap-1">
                <FocusIcon size={12} className={config.text} />
                {config.label} Mode
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Plan A/B Toggle */}
            <div className="flex bg-muted rounded-full p-1">
              <button
                onClick={() => handleVariantChange('A')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedVariant === 'A' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="plan-A-toggle"
              >
                Plan A
              </button>
              <button
                onClick={() => handleVariantChange('B')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  selectedVariant === 'B' 
                    ? 'bg-primary text-white shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="plan-B-toggle"
              >
                Plan B
              </button>
            </div>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Rationale Card */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className={`glass-card p-4 ${config.border} border-l-4`} data-testid="plan-rationale-card">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-xl ${config.bg}`}>
                <FocusIcon size={20} className={config.text} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-xs font-semibold uppercase ${config.text}`}>
                    {config.label} Day
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {selectedVariant === 'A' ? 'Primary' : 'Backup'} Plan
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {plan?.readiness_level} readiness
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground" data-testid="plan-why-text">
                  {plan?.rationale?.why_this_plan}
                </p>
                {plan?.adjustment_note && (
                  <div className="mt-2 flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span>{plan.adjustment_note}</span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </motion.section>

        {/* Weekly Focus */}
        {plan?.weekly_focus && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="glass-card p-3 bg-primary/5" data-testid="weekly-focus-card">
              <div className="flex items-center gap-2">
                <Target size={14} className="text-primary" />
                <p className="text-xs text-muted-foreground">{plan.weekly_focus}</p>
              </div>
            </Card>
          </motion.section>
        )}

        {/* Workout Section */}
        {currentPlan.workout && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Dumbbell size={18} className="text-primary" />
              <h2 className="font-heading font-semibold">Workout</h2>
            </div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
            >
              {(() => {
                const workout = currentPlan.workout;
                const isCompleted = completedItems.workouts.includes(workout.title);
                
                return (
                  <Card className={`glass-card p-4 ${isCompleted ? 'opacity-60' : ''}`} data-testid="workout-card">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded-xl ${config.bg} shrink-0`}>
                          <Dumbbell size={18} className={config.text} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs text-muted-foreground uppercase">{workout.workout_type}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
                              {workout.intensity}
                            </span>
                          </div>
                          <h3 className="font-medium text-sm mb-1" data-testid="workout-title">
                            <a
                              href={workout.justfit_url || `https://justfit.app/videos?search=${encodeURIComponent(workout.title)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-primary hover:underline transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {workout.title}
                              <ExternalLink size={10} className="inline ml-1 opacity-50" />
                            </a>
                          </h3>
                          <p className="text-xs text-muted-foreground line-clamp-2">{workout.description}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <Badge variant="secondary" className="text-xs">
                              <Clock size={10} className="mr-1" />
                              {workout.duration_minutes} min
                            </Badge>
                            {workout.equipment_needed?.length > 0 && (
                              <Badge variant="outline" className="text-xs">
                                {workout.equipment_needed.join(', ')}
                              </Badge>
                            )}
                            {workout.movement_type && (
                              <Badge variant="outline" className="text-xs capitalize">
                                {workout.movement_type.replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      {!isCompleted ? (
                        <div className="flex gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleLogWorkout(workout, false)}
                            disabled={loggingItem === workout.title}
                            className="h-8 w-8 p-0 rounded-full"
                            data-testid="skip-workout-btn"
                          >
                            <X size={14} className="text-muted-foreground" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleLogWorkout(workout)}
                            disabled={loggingItem === workout.title}
                            className="h-8 px-3 rounded-full bg-primary hover:bg-primary/90"
                            data-testid="complete-workout-btn"
                          >
                            <Check size={14} />
                          </Button>
                        </div>
                      ) : (
                        <div className="p-2 rounded-full bg-[#8BB89F]/20" data-testid="workout-completed-icon">
                          <Check size={16} className="text-[#8BB89F]" />
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })()}
            </motion.div>
          </section>
        )}

        {/* Meals Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Utensils size={18} className="text-primary" />
            <h2 className="font-heading font-semibold">Meals</h2>
          </div>
          <div className="space-y-3">
            {currentPlan.meals?.map((meal, index) => {
              const isCompleted = completedItems.meals.includes(meal.title);
              const MealIcon = mealIcons[meal.meal_type] || Utensils;
              
              return (
                <motion.div
                  key={`${meal.meal_type}-${meal.title}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (index + 1) * 0.1 + 0.2 }}
                >
                  <Card className={`glass-card p-4 ${isCompleted ? 'opacity-60' : ''}`} data-testid={`meal-card-${meal.meal_type}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="p-2 rounded-xl bg-primary/10 shrink-0">
                          <MealIcon size={18} className="text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs text-muted-foreground uppercase">{meal.meal_type}</span>
                            {meal.prep_time && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock size={10} />
                                {meal.prep_time}
                              </span>
                            )}
                          </div>
                          <h3 className="font-medium text-sm mb-1" data-testid={`meal-title-${meal.meal_type}`}>
                            <a
                              href={`https://www.allrecipes.com/search?q=${encodeURIComponent(meal.title)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-primary hover:underline transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {meal.title}
                              <ExternalLink size={10} className="inline ml-1 opacity-50" />
                            </a>
                          </h3>
                          <p className="text-xs text-muted-foreground line-clamp-2">{meal.description}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {meal.calories_estimate && (
                              <Badge variant="secondary" className="text-xs">
                                ~{meal.calories_estimate} cal
                              </Badge>
                            )}
                            {meal.macro_focus && (
                              <Badge variant="outline" className="text-xs capitalize">
                                {meal.macro_focus}
                              </Badge>
                            )}
                            {meal.dietary_tags?.slice(0, 2).map(tag => (
                              <Badge key={tag} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                      {!isCompleted ? (
                        <div className="flex gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleLogMeal(meal, true)}
                            disabled={loggingItem === meal.title}
                            className="h-8 w-8 p-0 rounded-full"
                            data-testid={`skip-meal-${meal.meal_type}`}
                          >
                            <X size={14} className="text-muted-foreground" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleLogMeal(meal)}
                            disabled={loggingItem === meal.title}
                            className="h-8 px-3 rounded-full bg-[#8BB89F] hover:bg-[#8BB89F]/90"
                            data-testid={`log-meal-${meal.meal_type}`}
                          >
                            <Check size={14} />
                          </Button>
                        </div>
                      ) : (
                        <div className="p-2 rounded-full bg-[#8BB89F]/20" data-testid={`meal-completed-${meal.meal_type}`}>
                          <Check size={16} className="text-[#8BB89F]" />
                        </div>
                      )}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Top Drivers */}
        {plan?.rationale?.top_drivers?.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="glass-card p-4" data-testid="top-drivers-card">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={14} className="text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">Key Factors Today</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {plan.rationale.top_drivers.map((driver, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {driver}
                  </Badge>
                ))}
              </div>
            </Card>
          </motion.section>
        )}

        {/* Summary / Progress */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="glass-card p-4 bg-primary/5" data-testid="progress-summary-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Today&apos;s Progress</p>
                <p className="text-xs text-muted-foreground">
                  {completedItems.meals.length}/{currentPlan.meals?.length || 3} meals
                  {currentPlan.workout && ` • ${completedItems.workouts.length}/1 workout`}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/coach')}
                className="rounded-full"
                data-testid="chat-with-coach-btn"
              >
                Chat with Coach
                <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </Card>
        </motion.section>

        {/* Plan B Note */}
        {selectedVariant === 'B' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <Info size={12} />
              Plan B preserves your streaks with achievable goals
            </p>
          </motion.div>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
